import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandsTab from "@/components/admin/BrandsTab";
import UsersTab from "@/components/admin/UsersTab";
import CategoriesTab from "@/components/admin/CategoriesTab";
import ProductsTab from "@/components/admin/ProductsTab";
import ContentTab from "@/components/admin/ContentTab";
import ThemeTab from "@/components/admin/ThemeTab";
import ScraperTab from "@/components/admin/ScraperTab";
import SalesTab from "@/components/admin/SalesTab";
import InvoiceTab from "@/components/admin/InvoiceTab";
import CustomersTab from "@/components/admin/CustomersTab";
import OrdersTab from "@/components/admin/OrdersTab";
import AccountTab from "@/components/admin/AccountTab";
import AnalyticsTab from "@/components/admin/AnalyticsTab";
import VisitorsTab from "@/components/admin/VisitorsTab";
import CatalogAuditTab from "@/components/admin/CatalogAuditTab";
import WhatsAppTab from "@/components/admin/WhatsAppTab";
import WaIcon from "@/components/whatsapp/WaIcon";
import AdminSidebar from "@/components/admin/AdminSidebar";
import AdminStats from "@/components/admin/AdminStats";
import "@/admin-dark.css";
import {
    Users as UsersIcon,
    Box,
    Database,
    Package,
    Layers,
    Palette,
    FileText,
    ScrollText,
    ShoppingCart,
    Receipt,
    User,
    Tag,
    BarChart3,
    Sun,
    Moon,
    Activity,
    ScanSearch,
} from "lucide-react";

export default function AdminPage() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get("tab") || "users";
    const setTab = (id) => setSearchParams({ tab: id }, { replace: true });
    const [stats, setStats] = useState(null);
    const [dark, setDark] = useState(() => localStorage.getItem("tc_admin_theme") !== "light");
    const toggleTheme = () => setDark((d) => {
        localStorage.setItem("tc_admin_theme", d ? "light" : "dark");
        return !d;
    });

    useEffect(() => {
        if (!user || user === false) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/stats");
                setStats(data);
            } catch {}
        })();
    }, [user, tab]);

    if (user === null) return null;
    if (!user || user.role !== "admin") return <Navigate to="/" replace />;

    const T = {
        users: { id: "users", label: "Users", icon: UsersIcon, badge: stats?.pending_users },
        orders: { id: "orders", label: "Orders", icon: Receipt, badge: stats?.pending_orders },
        customers: { id: "customers", label: "Customers", icon: UsersIcon },
        categories: { id: "categories", label: "Categories", icon: Layers, badge: stats?.categories },
        brands: { id: "brands", label: "Brands", icon: Tag },
        products: { id: "products", label: "Products", icon: Package, badge: stats?.products },
        sales: { id: "sales", label: "Sales", icon: ShoppingCart },
        invoice: { id: "invoice", label: "Invoice", icon: ScrollText },
        analytics: { id: "analytics", label: "Analytics", icon: BarChart3 },
        visitors: { id: "visitors", label: "Vizitorët", icon: Activity },
        content: { id: "content", label: "Content", icon: FileText },
        theme: { id: "theme", label: "Theme", icon: Palette },
        account: { id: "account", label: "Account", icon: User },
        scraper: { id: "scraper", label: "Import", icon: Database },
        audit: { id: "audit", label: "Kontrolli i katalogut", icon: ScanSearch },
        whatsapp: { id: "whatsapp", label: "WhatsApp", icon: WaIcon },
    };
    const groups = [
        { title: "Shitje", items: [T.sales, T.invoice, T.orders, T.customers, T.whatsapp, T.analytics, T.visitors] },
        { title: "Katalog", items: [T.products, T.categories, T.brands, T.scraper, T.audit] },
        { title: "Dizajn", items: [T.content, T.theme] },
        { title: "Cilësime", items: [T.users, T.account] },
    ];
    const statItems = [
        { icon: UsersIcon, label: "Users", val: stats?.users, sub: stats?.pending_users ? `${stats.pending_users} pending` : "—" },
        { icon: Layers, label: "Categories", val: stats?.categories },
        { icon: Package, label: "Products", val: stats?.products },
        { icon: Box, label: "Pending", val: stats?.pending_users, accent: stats?.pending_users > 0 },
        { icon: Database, label: "DB Records", val: (stats?.categories || 0) + (stats?.products || 0) + (stats?.users || 0) },
    ];
    const current = T[tab] || T.users;

    return (
        <div className={`${dark ? "admin-scope" : "admin-shell-light"} min-h-[80vh]`} data-testid="admin-page" data-theme={dark ? "dark" : "light"}>
            <div className="max-w-[1600px] mx-auto px-4 py-6 flex flex-col lg:flex-row gap-6">
                <AdminSidebar groups={groups} tab={tab} setTab={setTab} />
                <main className="flex-1 min-w-0 space-y-6">
                    <header className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="label-kicker flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] adm-pulse" /> Admin Control
                            </div>
                            <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight adm-heading" data-testid="admin-page-title">
                                Control Room <span className="text-[hsl(var(--primary))]">/</span> <span className="text-slate-400">{current.label}</span>
                            </h1>
                        </div>
                        <button type="button" onClick={toggleTheme} className="adm-theme-toggle" title={dark ? "Light mode" : "Dark mode"} data-testid="admin-theme-toggle">
                            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                    </header>
                    <AdminStats items={statItems} />
                    <section key={tab} className="adm-panel adm-fade-in p-4 md:p-6" data-testid="admin-tab-panel">
                        {tab === "users" && <UsersTab />}
                        {tab === "orders" && <OrdersTab />}
                        {tab === "customers" && <CustomersTab />}
                        {tab === "categories" && <CategoriesTab />}
                        {tab === "brands" && <BrandsTab />}
                        {tab === "products" && <ProductsTab />}
                        {tab === "sales" && <SalesTab />}
                        {tab === "invoice" && <InvoiceTab />}
                        {tab === "analytics" && <AnalyticsTab />}
                        {tab === "visitors" && <VisitorsTab />}
                        {tab === "audit" && <CatalogAuditTab />}
                        {tab === "content" && <ContentTab />}
                        {tab === "theme" && <ThemeTab />}
                        {tab === "account" && <AccountTab />}
                        {tab === "scraper" && <ScraperTab />}
                        {tab === "whatsapp" && <WhatsAppTab />}
                    </section>
                </main>
            </div>
        </div>
    );
}
